import { useStore } from "../state/store";
import { validateNamePart } from "../model/validation";
import { EmptyState, FullIdPreview, LocalizedField } from "../ui/common";
import { LocaleSwitcher } from "../ui/LocaleSwitcher";
import { readFileAsDataUrl } from "../ui/files";
import type { Item } from "../model/types";

const CATEGORIES: Item["category"][] = ["construction", "equipment", "items", "nature", "none"];

export function ItemsPanel() {
  const project = useStore((s) => s.project);
  const selectedUuid = useStore((s) => s.ui.selectedItemUuid);
  const selectItem = useStore((s) => s.selectItem);
  const addItem = useStore((s) => s.addItem);
  const removeItem = useStore((s) => s.removeItem);

  const selected = project.items.find((i) => i.uuid === selectedUuid) ?? project.items[0];

  return (
    <div className="panel split">
      <div className="list-column">
        <div className="list-header">
          <h2>Items</h2>
          <button className="btn primary" onClick={() => addItem()}>
            + Item
          </button>
        </div>
        <p className="hint">Custom items that recipes can produce. The guide item is configured in Settings.</p>
        <ul className="entity-list">
          {project.items.map((item) => (
            <li
              key={item.uuid}
              className={item.uuid === selected?.uuid ? "active" : ""}
              onClick={() => selectItem(item.uuid)}
            >
              <span className="entity-name">{item.id}</span>
              <FullIdPreview namespace={project.namespace} id={item.id} />
            </li>
          ))}
          {project.items.length === 0 && <li className="muted">No items yet.</li>}
        </ul>
      </div>

      <div className="editor-column">
        {selected ? (
          <ItemEditor key={selected.uuid} item={selected} onDelete={() => removeItem(selected.uuid)} />
        ) : (
          <EmptyState>Add an item to get started.</EmptyState>
        )}
      </div>
    </div>
  );
}

function ItemEditor({ item, onDelete }: { item: Item; onDelete: () => void }) {
  const namespace = useStore((s) => s.project.namespace);
  const activeLocale = useStore((s) => s.ui.activeLocale);
  const updateItem = useStore((s) => s.updateItem);
  const renameItem = useStore((s) => s.renameItem);

  const idValid = validateNamePart(item.id);

  const onTexture = async (file: File) => {
    const dataUrl = await readFileAsDataUrl(file);
    updateItem(item.uuid, { textureDataUrl: dataUrl });
  };

  return (
    <div className="editor">
      <LocaleSwitcher />

      <label className="field">
        <span className="field-label">Name-part (id)</span>
        <input
          className={idValid ? "" : "invalid"}
          value={item.id}
          spellCheck={false}
          onChange={(e) => renameItem(item.uuid, e.target.value.trim())}
        />
      </label>
      <div className="preview-line">
        Full id: <FullIdPreview namespace={namespace} id={item.id} /> · Texture short-name:{" "}
        <FullIdPreview namespace={namespace} id={item.id} />
      </div>
      {!idValid && <p className="error-text">Id must match ^[a-z0-9_]+$</p>}

      <LocalizedField
        label="Display name"
        locale={activeLocale}
        value={item.displayName}
        onChange={(displayName) => updateItem(item.uuid, { displayName })}
      />

      <label className="field">
        <span className="field-label">Creative menu category</span>
        <select value={item.category} onChange={(e) => updateItem(item.uuid, { category: e.target.value as Item["category"] })}>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>

      <div className="field">
        <span className="field-label">Texture (16×16 PNG)</span>
        <div className="texture-row">
          <div className="texture-preview">
            {item.textureDataUrl ? <img src={item.textureDataUrl} alt="" /> : <span className="muted">placeholder</span>}
          </div>
          <label className="btn">
            Upload PNG…
            <input type="file" accept="image/png" hidden onChange={(e) => e.target.files?.[0] && onTexture(e.target.files[0])} />
          </label>
          {item.textureDataUrl && (
            <button className="btn" onClick={() => updateItem(item.uuid, { textureDataUrl: undefined })}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="editor-footer">
        <button className="btn danger" onClick={onDelete}>
          Delete item
        </button>
      </div>
    </div>
  );
}
